// Textur- und Strichverwaltung für Graffiti.
//
// Warum getrennt von paint.js: diese Datei darf nichts über Eingabe oder Netz
// wissen — dadurch ist die teure Pixelarbeit isoliert und ohne Browser testbar
// (injizierbare Canvas-Fabrik). Konzept §6: Striche als Vektordaten halten,
// Pixel nur als abgeleitete Ansicht — sonst wäre LOD-Wechsel unmöglich.

import * as THREE from 'three';
import { PAINT_MAX_STROKES_PER_SURFACE } from '../../shared/constants.js';

/** Hartes Budget: VR-tauglich und genug für den Zug in der Nähe (Konzept §14). */
export const TEXTURE_BUDGET = 24;
/** Leere Flächen jenseits davon kosten gar nichts (Konzept §6.2: Leere ist Normalfall). */
export const EVICT_EMPTY_M = 60;
/** Bemalte Flächen länger halten — Neuzeichnen aller Striche beim Umdrehen vermeiden. */
export const EVICT_PAINTED_M = 200;
/** Ab dieser Distanz reicht halbe Auflösung; nah und beim Malen volle pxW/pxH. */
export const RES_FULL_M = 18;

// ---------------------------------------------------------------------------
// Fabriken — im Browser DOM/Three, im Test ein Fake ohne Allokationskosten
// ---------------------------------------------------------------------------

let _canvasFactory = null;
let _textureFactory = null;

/** @param {(w:number,h:number)=>HTMLCanvasElement|object|null} fn */
export function setCanvasFactory(fn) { _canvasFactory = fn; }
/** @param {(canvas:object)=>{dispose:Function,needsUpdate:boolean,image?:object}|null} fn */
export function setTextureFactory(fn) { _textureFactory = fn; }
export function resetFactories() { _canvasFactory = null; _textureFactory = null; }

function makeCanvas(w, h) {
  const cw = Math.max(1, w | 0);
  const ch = Math.max(1, h | 0);
  if (_canvasFactory) {
    const c = _canvasFactory(cw, ch);
    if (c) { c.width = cw; c.height = ch; }
    return c;
  }
  const c = document.createElement('canvas');
  c.width = cw;
  c.height = ch;
  return c;
}

function makeTexture(canvas) {
  if (_textureFactory) return _textureFactory(canvas);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  // Canvas-Y zeigt nach unten, Three-UV nach oben — flipY hält den Strich richtig herum.
  tex.flipY = true;
  return tex;
}

// ---------------------------------------------------------------------------
// Strich-Hilfen (rein, allokationsarm — Aufrufer liefert Zielarrays)
// ---------------------------------------------------------------------------

/** Punkte in 0..1 klemmen. Mutiert `pts` in-place. */
export function clampStrokePts(pts) {
  for (let i = 0; i < pts.length; i++) {
    const v = pts[i];
    pts[i] = v < 0 ? 0 : v > 1 ? 1 : v;
  }
  return pts;
}

/**
 * Punktdezimierung in Metern auf der Fläche.
 * `out` wird geleert und mit dezierten [u,v,...] gefüllt. Kein neues Array.
 */
export function decimatePoints(pts, widthM, heightM, minDistM, out) {
  out.length = 0;
  if (!pts || pts.length < 2) return out;
  const minD2 = minDistM * minDistM;
  let lu = pts[0], lv = pts[1];
  out.push(lu, lv);
  for (let i = 2; i < pts.length; i += 2) {
    const u = pts[i], v = pts[i + 1];
    const du = (u - lu) * widthM;
    const dv = (v - lv) * heightM;
    if (du * du + dv * dv >= minD2) {
      out.push(u, v);
      lu = u; lv = v;
    }
  }
  // Letzten Punkt behalten, sonst endet der Strich vor dem Loslassen.
  const lastU = pts[pts.length - 2], lastV = pts[pts.length - 1];
  if (out[out.length - 2] !== lastU || out[out.length - 1] !== lastV) {
    out.push(lastU, lastV);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Spray-Look: weiche Stempel + Tröpfchen, kein harter Stroke
// ---------------------------------------------------------------------------

/**
 * Zeichnet einen Strich auf einen 2D-Kontext.
 * spray = weich/breit/transparent, marker = deckend/schmal (Konzept §14).
 */
export function drawStrokeOnContext(ctx2d, stroke, pxW, pxH) {
  const pts = stroke.pts;
  if (!pts || pts.length < 2) return;

  const tool = stroke.tl === 'marker' ? 'marker' : 'spray';
  const color = typeof stroke.c === 'string' ? stroke.c : '#f2f0e6';
  // width ist Flächenanteil (0..~0.35) — in Pixel umrechnen.
  const baseR = Math.max(1.2, (stroke.w || 0.03) * Math.min(pxW, pxH) * 0.5);
  const radius = tool === 'marker' ? baseR * 0.55 : baseR * 1.15;
  const step = Math.max(0.35, radius * (tool === 'marker' ? 0.45 : 0.38));

  // Deterministischer Rausch-Seed aus erstem Punkt — Tröpfchen flackern nicht.
  let seed = (pts[0] * 1009 + pts[1] * 7919) | 0;

  const stamp = (x, y, r, alpha) => {
    if (tool === 'marker') {
      ctx2d.globalAlpha = Math.min(1, alpha * 1.4);
      ctx2d.fillStyle = color;
      ctx2d.beginPath();
      ctx2d.arc(x, y, r, 0, Math.PI * 2);
      ctx2d.fill();
      return;
    }
    // Radialer Nebel: Mitte deckender, Rand transparent — der typische Sprühlook.
    const g = ctx2d.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, color);
    g.addColorStop(0.45, color);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx2d.globalAlpha = alpha;
    ctx2d.fillStyle = g;
    ctx2d.beginPath();
    ctx2d.arc(x, y, r, 0, Math.PI * 2);
    ctx2d.fill();
  };

  const nextRand = () => {
    seed = (seed * 1664525 + 1013904223) | 0;
    return (seed >>> 0) / 4294967296;
  };

  ctx2d.save();
  ctx2d.globalCompositeOperation = 'source-over';

  // Einzelpunkt: trotzdem ein Klecks, sonst fühlt sich Tippen tot an.
  if (pts.length === 2) {
    const x = pts[0] * pxW, y = pts[1] * pxH;
    stamp(x, y, radius, tool === 'marker' ? 0.92 : 0.22);
    if (tool === 'spray') {
      for (let k = 0; k < 4; k++) {
        const a = nextRand() * Math.PI * 2;
        const d = nextRand() * radius * 1.4;
        stamp(x + Math.cos(a) * d, y + Math.sin(a) * d, radius * 0.2, 0.08);
      }
    }
    ctx2d.restore();
    ctx2d.globalAlpha = 1;
    return;
  }

  for (let i = 0; i < pts.length - 2; i += 2) {
    const x0 = pts[i] * pxW, y0 = pts[i + 1] * pxH;
    const x1 = pts[i + 2] * pxW, y1 = pts[i + 3] * pxH;
    const dx = x1 - x0, dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));
    for (let s = 0; s <= n; s++) {
      const t = s / n;
      const x = x0 + dx * t;
      const y = y0 + dy * t;
      if (tool === 'marker') {
        stamp(x, y, radius, 0.85);
      } else {
        stamp(x, y, radius, 0.16);
        // Leichte Streuung: ein paar Tröpfchen neben dem Strahl.
        if (nextRand() < 0.35) {
          const a = nextRand() * Math.PI * 2;
          const d = radius * (0.6 + nextRand() * 1.1);
          stamp(x + Math.cos(a) * d, y + Math.sin(a) * d, radius * (0.12 + nextRand() * 0.2), 0.07);
        }
      }
    }
  }

  ctx2d.restore();
  ctx2d.globalAlpha = 1;
}

// ---------------------------------------------------------------------------
// SurfaceManager
// ---------------------------------------------------------------------------

/**
 * @typedef {object} SurfaceMeta
 * @property {string} surfaceId
 * @property {number} pxW
 * @property {number} pxH
 * @property {number} [widthM]
 * @property {number} [heightM]
 * @property {object} [mesh] THREE.Mesh mit userData.surfaceId
 */

/**
 * Hält Vektorstriche und allokiert Canvas-Texturen lazy.
 * Eine leere, ferne Fläche kostet null — das ist die harte Anforderung.
 */
export class SurfaceManager {
  /**
   * @param {object} [opts]
   * @param {number} [opts.budget]
   * @param {number} [opts.evictEmptyM]
   * @param {number} [opts.evictPaintedM]
   */
  constructor(opts = {}) {
    this.budget = opts.budget ?? TEXTURE_BUDGET;
    this.evictEmptyM = opts.evictEmptyM ?? EVICT_EMPTY_M;
    this.evictPaintedM = opts.evictPaintedM ?? EVICT_PAINTED_M;

    /** @type {Map<string, SurfaceEntry>} */
    this.entries = new Map();
    /** Lebende Texturen in LRU-Reihenfolge (Index 0 = am längsten unbenutzt). */
    this._lru = [];
    this._clock = 1;
    /** Zählt texture.needsUpdate — Tests prüfen Batching damit. */
    this.textureUpdates = 0;
    /** Fläche, die gerade bemalt wird — #alloc und maintain dürfen sie nicht kicken. */
    this.pinnedId = null;
  }

  /** Fläche registrieren / Metadaten aktualisieren. Allokiert noch nichts. */
  register(meta) {
    let e = this.entries.get(meta.surfaceId);
    if (!e) {
      e = new SurfaceEntry(meta.surfaceId);
      this.entries.set(meta.surfaceId, e);
    }
    e.basePxW = meta.pxW | 0 || e.basePxW || 64;
    e.basePxH = meta.pxH | 0 || e.basePxH || 64;
    e.widthM = meta.widthM ?? e.widthM ?? 1;
    e.heightM = meta.heightM ?? e.heightM ?? 1;
    if (meta.mesh) e.mesh = meta.mesh;
    return e;
  }

  get(surfaceId) { return this.entries.get(surfaceId) ?? null; }

  /** Anzahl aktuell lebender GPU-Texturen. */
  liveTextureCount() { return this._lru.length; }

  /**
   * paintData: alle Striche ersetzen, einmal zeichnen, einmal needsUpdate.
   * Autoritative Serverantwort — lokale Optimistik wird überschrieben.
   */
  setStrokes(surfaceId, strokes) {
    const e = this.entries.get(surfaceId) ?? this.register({ surfaceId, pxW: 256, pxH: 128 });
    e.strokes = Array.isArray(strokes) ? strokes.slice() : [];
    e.hasPaint = e.strokes.length > 0;
    this.touch(surfaceId);
    if (!e.hasPaint && !e.canvas) return e;

    // #ensureCanvas zeichnet bei Erstallokation/LOD bereits alle Striche.
    // Bei unveränderter Canvas müssen wir hier genau einmal neu aufbauen.
    const had = !!e.canvas;
    const prevW = e.pxW, prevH = e.pxH;
    this.#ensureCanvas(e, this.#wantFull(e));
    if (e.ctx2d && had && e.pxW === prevW && e.pxH === prevH) {
      this.#redrawAll(e);
    }
    return e;
  }

  /**
   * Inkrementell einen Strich oben drauf (Konzept §6.4: Übermalen = drüber malen).
   * Nur der neue Strich wird gezeichnet — kein Full-Redraw.
   */
  addStroke(surfaceId, stroke) {
    if (!stroke?.pts || stroke.pts.length < 2) return null;
    const e = this.entries.get(surfaceId) ?? this.register({ surfaceId, pxW: 256, pxH: 128 });
    // pts absichern, ohne das Original des Callers zu mutieren falls shared.
    const pts = stroke.pts.slice();
    clampStrokePts(pts);
    const clean = {
      s: surfaceId,
      c: stroke.c,
      w: stroke.w,
      pts,
      tl: stroke.tl === 'marker' ? 'marker' : 'spray',
      ws: stroke.ws,
    };
    e.strokes.push(clean);
    e.hasPaint = true;
    // Gleiches Dach wie Server (store.js) — sonst wächst der Client unbegrenzt.
    let trimmed = false;
    if (e.strokes.length > PAINT_MAX_STROKES_PER_SURFACE) {
      const drop = Math.floor(PAINT_MAX_STROKES_PER_SURFACE / 4);
      e.strokes.splice(0, drop);
      trimmed = true;
    }
    this.touch(surfaceId);

    // Vorherigen Canvas-Zustand merken: bei Erstallokation/LOD zeichnet
    // #ensureCanvas bereits alle Striche (inkl. diesem). Nur auf stabiler
    // Canvas wird inkrementell gestempelt — sonst doppelte Pixel + 2 Updates.
    const hadCanvas = !!e.canvas;
    const prevW = e.pxW, prevH = e.pxH;
    this.#ensureCanvas(e, this.#wantFull(e));
    if (!e.ctx2d) return e;
    if (trimmed) {
      // Älteste Striche weg → Voll-Redraw, sonst bleiben Pixel von Verworfenem.
      this.#redrawAll(e);
    } else if (hadCanvas && e.pxW === prevW && e.pxH === prevH) {
      drawStrokeOnContext(e.ctx2d, clean, e.pxW, e.pxH);
      this.#markTexture(e);
    }
    return e;
  }

  /** Volle Auflösung nah am Spieler oder solange Distanz noch unbekannt ist. */
  #wantFull(e) {
    if (!Number.isFinite(e.distM)) return true;
    return e.distM < RES_FULL_M;
  }

  /** LRU-Touch: Fläche wurde angeschaut / bemalt. */
  touch(surfaceId) {
    const e = this.entries.get(surfaceId);
    if (!e) return;
    e.lastUsed = this._clock++;
    if (e.texture) this.#lruBump(surfaceId);
  }

  /** Distanz zum Spieler setzen (Meter). Steuert LOD und Auslagerung. */
  setDistance(surfaceId, distM) {
    const e = this.entries.get(surfaceId);
    if (e) e.distM = distM;
  }

  /**
   * Canvas für Malen vorbereiten (lazy). fullRes=true wenn Spieler nah sprüht.
   * Leere Fläche ohne fullRes und ohne Striche: keine Allokation.
   */
  ensureForPainting(surfaceId, fullRes = true) {
    const e = this.entries.get(surfaceId);
    if (!e) return null;
    this.touch(surfaceId);
    this.#ensureCanvas(e, fullRes);
    return e;
  }

  /**
   * LOD / Auslagerung. Aufrufer liefert aktuelle Distanzen (nicht pro Frame nötig).
   * @param {Map<string, number>|Record<string,number>} distances
   * @param {string|null} keepId Fläche die gerade bemalt wird — nie freigeben
   */
  maintain(distances, keepId = null) {
    const prevPin = this.pinnedId;
    if (keepId) this.pinnedId = keepId;

    if (distances) {
      if (distances instanceof Map) {
        for (const [id, d] of distances) this.setDistance(id, d);
      } else {
        for (const id of Object.keys(distances)) this.setDistance(id, distances[id]);
      }
    }

    // LOD: ferne bemalte Flächen runter, nahe hoch.
    for (const id of [...this._lru]) {
      const e = this.entries.get(id);
      if (!e?.canvas) continue;
      if (id === this.pinnedId) {
        this.#ensureCanvas(e, true);
        continue;
      }
      this.#ensureCanvas(e, this.#wantFull(e));
    }

    // Distanz-Auslagerung.
    for (let i = this._lru.length - 1; i >= 0; i--) {
      const id = this._lru[i];
      if (id === this.pinnedId) continue;
      const e = this.entries.get(id);
      if (!e) { this._lru.splice(i, 1); continue; }
      const limit = e.hasPaint ? this.evictPaintedM : this.evictEmptyM;
      if (e.distM > limit) this.#freeTexture(e);
    }

    // Hartes Budget: LRU fliegt zuerst.
    while (this._lru.length > this.budget) {
      const victim = this.#pickVictim(null);
      if (!victim) break;
      this.#freeTexture(victim);
    }

    this.pinnedId = prevPin;
  }

  dispose() {
    for (const e of this.entries.values()) this.#freeTexture(e);
    this.entries.clear();
    this._lru.length = 0;
  }

  // --- intern ---------------------------------------------------------------

  #ensureCanvas(e, fullRes) {
    const tw = fullRes
      ? e.basePxW
      : Math.max(32, e.basePxW >> 1);
    const th = fullRes
      ? e.basePxH
      : Math.max(32, e.basePxH >> 1);

    if (e.canvas && e.pxW === tw && e.pxH === th) {
      this.#bindMaterial(e);
      return;
    }

    // Auflösungswechsel oder Erstallokation: alles neu, Striche bleiben als Vektoren.
    const had = !!e.canvas;
    this.#freeTexture(e, /*keepStrokes*/ true);

    // Ohne Inhalt und ohne Malwunsch lohnt sich keine Allokation.
    if (!e.hasPaint && !fullRes && !had) return;

    this.#alloc(e, tw, th);
    if (e.hasPaint) this.#redrawAll(e);
    else this.#markTexture(e); // leere Canvas trotzdem einmal pushen
  }

  #pickVictim(exceptId) {
    let victim = null;
    let victimUsed = Infinity;
    for (const id of this._lru) {
      if (id === exceptId || id === this.pinnedId) continue;
      const o = this.entries.get(id);
      if (!o) continue;
      if (o.lastUsed < victimUsed) {
        victimUsed = o.lastUsed;
        victim = o;
      }
    }
    return victim;
  }

  /**
   * Overlay-Planes (train.js: userData.isPaint) liegen *auf* der Wand und
   * müssen transparent starten. Hofmauern *sind* die Wand — transparenter
   * Canvas würde sie durchsichtig machen. Deshalb deckend weiß füllen.
   */
  #isPaintOverlay(e) {
    const src = e.baseMaterial ?? e.mesh?.material;
    const m = Array.isArray(src) ? src[0] : src;
    return !!(m?.userData?.isPaint);
  }

  #clearCanvas(e) {
    if (!e.ctx2d) return;
    if (this.#isPaintOverlay(e)) {
      e.ctx2d.clearRect(0, 0, e.pxW, e.pxH);
    } else {
      e.ctx2d.globalAlpha = 1;
      e.ctx2d.globalCompositeOperation = 'source-over';
      e.ctx2d.fillStyle = '#ffffff';
      e.ctx2d.fillRect(0, 0, e.pxW, e.pxH);
    }
  }

  #alloc(e, w, h) {
    // Vor dem Allokieren Platz schaffen (pinned + Ziel-Fläche bleiben).
    while (this._lru.length >= this.budget) {
      const victim = this.#pickVictim(e.surfaceId);
      if (!victim) break;
      this.#freeTexture(victim);
    }

    const canvas = makeCanvas(w, h);
    if (!canvas) return;
    const ctx2d = canvas.getContext('2d', { alpha: true, desynchronized: true });
    if (!ctx2d) return;

    e.canvas = canvas;
    e.ctx2d = ctx2d;
    e.pxW = w;
    e.pxH = h;
    // baseMaterial erst nach #bindMaterial gesetzt — Overlay an Original-Mesh erkennen.
    this.#clearCanvas(e);
    e.texture = makeTexture(canvas);
    this._lru.push(e.surfaceId);
    this.#bindMaterial(e);
  }

  #redrawAll(e) {
    if (!e.ctx2d) return;
    this.#clearCanvas(e);
    for (let i = 0; i < e.strokes.length; i++) {
      drawStrokeOnContext(e.ctx2d, e.strokes[i], e.pxW, e.pxH);
    }
    this.#markTexture(e);
  }

  #markTexture(e) {
    if (!e.texture) return;
    e.texture.needsUpdate = true;
    this.textureUpdates++;
  }

  #bindMaterial(e) {
    const mesh = e.mesh;
    if (!mesh || !e.texture) return;
    // Geteilte Materialien (style.mat Cache) nicht mutieren — sonst färbt ein
    // Waggon alle anderen mit derselben Farbe um.
    if (!e.ownMaterial) {
      const base = mesh.material;
      e.baseMaterial = base;
      e.ownMaterial = Array.isArray(base)
        ? base.map((m) => m.clone())
        : base.clone();
      mesh.material = e.ownMaterial;
    }
    const mat = Array.isArray(e.ownMaterial) ? e.ownMaterial[0] : e.ownMaterial;
    const overlay = this.#isPaintOverlay(e);
    if (mat.map !== e.texture) {
      mat.map = e.texture;
      // Overlay: transparent über dem Waggonkörper. Wand-selbst: deckend (weißer Canvas-BG).
      mat.transparent = overlay;
      if (!overlay) {
        mat.opacity = 1;
        mat.alphaTest = 0;
      }
      mat.needsUpdate = true;
    }
  }

  #unbindMaterial(e) {
    if (!e.mesh) return;
    if (e.ownMaterial) {
      const mats = Array.isArray(e.ownMaterial) ? e.ownMaterial : [e.ownMaterial];
      for (const m of mats) {
        m.map = null;
        m.dispose?.();
      }
      e.ownMaterial = null;
    }
    if (e.baseMaterial) {
      e.mesh.material = e.baseMaterial;
      e.baseMaterial = null;
    }
  }

  #freeTexture(e, keepStrokes = true) {
    if (!e) return;
    const idx = this._lru.indexOf(e.surfaceId);
    if (idx >= 0) this._lru.splice(idx, 1);

    if (e.texture) {
      e.texture.dispose?.();
      e.texture = null;
    }
    this.#unbindMaterial(e);
    e.canvas = null;
    e.ctx2d = null;
    e.pxW = 0;
    e.pxH = 0;
    if (!keepStrokes) {
      e.strokes.length = 0;
      e.hasPaint = false;
    }
  }

  #lruBump(surfaceId) {
    const i = this._lru.indexOf(surfaceId);
    if (i < 0) return;
    if (i === this._lru.length - 1) return;
    this._lru.splice(i, 1);
    this._lru.push(surfaceId);
  }
}

class SurfaceEntry {
  constructor(surfaceId) {
    this.surfaceId = surfaceId;
    this.strokes = [];
    this.hasPaint = false;
    this.basePxW = 64;
    this.basePxH = 64;
    this.pxW = 0;
    this.pxH = 0;
    this.widthM = 1;
    this.heightM = 1;
    this.distM = Infinity;
    this.lastUsed = 0;
    this.mesh = null;
    this.canvas = null;
    this.ctx2d = null;
    this.texture = null;
    this.ownMaterial = null;
    this.baseMaterial = null;
  }
}
